import {
  HealthEventNotificationSetupV1,
  HealthEventNotificationV1,
  NotificationJobConfigV1,
  NotificationJobConfigV1Schema,
  PatientInactiveNotificationV1,
} from '@csp/config-schemas';
import { EnumUtil } from '@csp/csp-common-enum-util';
import { HealthEventNotificationJobConfig, HealthEventNotificationJobConfigs } from '@csp/csp-common-healthevent';
import { EmailJobConfig, Maybe } from '@csp/csp-common-model';
import { CommonHcpWebPath } from '@csp/csp-common-routes';
import { JsonValidationService, isDefined } from '@csp/csp-common-util';
import { RoleType } from '@csp/dmdp-api-common-dto';

type NotificationJobConfigServiceV1Return = {
  healthEventNotificationJobConfigs: HealthEventNotificationJobConfigs;
  patientInactiveEmailConfigs: EmailJobConfig[];
};

const JSON_SCHEMA = 'notification-job-config-v1.json';

const toRoleTypes = (roles: string[]): RoleType[] =>
  roles.map(roleAsString => EnumUtil.fromString<RoleType>(roleAsString, RoleType)).filter(isDefined);

const toPatientColumnLink = (patientColumnLink: Maybe<string>): Maybe<CommonHcpWebPath> =>
  EnumUtil.fromMaybeString<CommonHcpWebPath>(patientColumnLink, CommonHcpWebPath);

// Health event mapping
const toHealthEventNotificationJobConfig = (
  notificationJobConfigV1?: HealthEventNotificationSetupV1,
): Maybe<HealthEventNotificationJobConfig> =>
  notificationJobConfigV1
    ? {
        enabled: notificationJobConfigV1.enabled,
        roles: toRoleTypes(notificationJobConfigV1.roles),
        sinceSecs: notificationJobConfigV1.minAgeOfEventSecs,
        patientColumnLink: toPatientColumnLink(notificationJobConfigV1.patientColumnLink),
      }
    : undefined;

const toHealthEventNotificationJobConfigs = (
  healthEventNotificationV1?: HealthEventNotificationV1,
): HealthEventNotificationJobConfigs => ({
  sendOnceNotificationConfig: toHealthEventNotificationJobConfig(healthEventNotificationV1?.once),
  sendRepeatedlyNotificationConfig: toHealthEventNotificationJobConfig(healthEventNotificationV1?.repeatedly),
});

const toEmailJobConfigs = (patientInactiveNotification: Maybe<PatientInactiveNotificationV1>): EmailJobConfig[] =>
  patientInactiveNotification?.emails?.map(({ code, cronPattern, roles, patientColumnLink, groupBy }) =>
    EmailJobConfig.from({ code, cronPattern, roles: toRoleTypes(roles), patientColumnLink, groupBy }),
  ) ?? [];

const mapNotificationJobConfig = (
  notificationJobConfigV1: NotificationJobConfigV1,
): NotificationJobConfigServiceV1Return => {
  JsonValidationService.validateJsonNonStrict(JSON_SCHEMA, NotificationJobConfigV1Schema, notificationJobConfigV1);

  return {
    healthEventNotificationJobConfigs: toHealthEventNotificationJobConfigs(
      notificationJobConfigV1.healthEventNotification,
    ),
    patientInactiveEmailConfigs: toEmailJobConfigs(notificationJobConfigV1.patientInactiveNotification),
  };
};

export const NotificationJobConfigServiceV1 = {
  mapNotificationJobConfig,
};
